import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode, useLayoutEffect, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'

/** Reveal the app as soon as it commits — fonts are non-blocking. */
function FirstPaint({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    document.getElementById('page-loading')?.remove()
  }, [])

  return children
}

/**
 * Keep `<html lang>` on the locale the page was rendered in.
 *
 * Rails writes it on the first paint, but the EN/RU switch is an
 * Inertia visit: it replaces the page props and leaves the document's
 * own attributes alone. The display face is picked off that attribute
 * (`html[lang="ru"]` swaps the graffiti hand for Unbounded, which has
 * Cyrillic), so a stale `lang` leaves Russian headlines on Inter.
 */
function syncLang(page: { props: Record<string, unknown> }) {
  const locale = page.props.locale
  if (typeof locale === 'string') document.documentElement.lang = locale
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page: ReactNode) => (<Layout>{page}</Layout>)

    return page
  },

  setup({ el, App, props }) {
    syncLang(props.initialPage)
    // Both, because neither covers the other: `success` is the only one
    // that fires for an ordinary visit — which is what the EN/RU switch
    // is — and `navigate` is the only one that fires for a back/forward
    // restore, where the page comes out of the history cache and no
    // request is made at all.
    router.on('success', (event) => syncLang(event.detail.page))
    router.on('navigate', (event) => syncLang(event.detail.page))

    createRoot(el).render(
      <StrictMode>
        <FirstPaint>
          <App {...props} />
        </FirstPaint>
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
